
// Vue, third party
import { Component, Watch, Vue, Mixins, Prop } from "vue-property-decorator";
import { State, Getter, Action, Mutation, namespace } from "vuex-class";
import draggable from "vuedraggable"; // @ref https://github.com/SortableJS/Sortable#options


import { TreeView, TreeItemContent, sortedValue } from "@/types/treeView";

@Component({
  components: {
    "tree-item": () => import("./TreeItem.vue"),
    draggable
  }
})
export default class TreeItem extends Vue {

    // Current tree level item
    @Prop({ default: null, type: Object }) item: TreeView;

    // To expand or collapse the full tree
    @Prop({ default: false, type: Boolean }) expandTree: boolean = false;

    // current level
    @Prop({ default: 0, type: Number }) level: number;

    // to enable or disable drag
    @Prop({ default: false, type: Boolean }) disableDrag: boolean;

    // amount of children.
    @Prop({ default: 0, type: Number }) relativeCount: number;

    // Spacing for the indent of the tree per parent to children.
    @Prop({ default: 0, type: Number }) spacing: number;

    // We we allow the ability of sorting
    @Prop({ default: true, type: Boolean }) sorting: boolean;

    // If we want the full tree to lose ability and be disabled.
    @Prop({ default: false, type: Boolean }) disableTree: boolean;

    @Prop({ default: 0, type: Number }) sortLevel: number;

    @Prop({ default: () => { return {
        animation: 0,
        disabled: false,
        ghostClass: "action-plan-item-ghost"
    }}, type: Object }) actionPlanDragOptions;

    /**
     * @summary Watcher that sets the current tree state to collapse or expand
     * depending on the most parent value.
     * @returns void
     */
    @Watch("expandTree")
    private setTreeItemOpenStatus(isOpen: boolean) {
      if (!isOpen) {
        this.isOpen = isOpen;
        return;
      }
      this.isOpen = isOpen;
    }

    // If the current tree has expanded or collpased it's children.
    private isOpen: boolean = false;

    // Flag on which the tree items are being moved or not.
    private reorderingItem: boolean = false;

    /**
     * @summary Computed property which will return the children arr length
     * @returns current number of children.
     */
    private get hasChildren() {
      return (this.item && this.item.children && this.item.children.length > 0) ? this.item.children.length : 0;
    }

    /**
     * @summary Computed property which confirms the current tree context.
     * @returns returns TreeItemContent
     */
    private get treeItemContent(): TreeItemContent {
        const content: TreeItemContent = {
          item: this.item,
          level: this.level,
          disableDrag: this.disableDrag,
          relativeCount: this.relativeCount,
          isOpen: this.isOpen,
          reorderingItem: this.reorderingItem,
          disabled: this.disableTree,
          additional : this.item.additional
        }
        return content;
    }

    /**
     * @summary handler which gets trigger when the tree item is transformed on 
     * the current tree level.
     * @param moved object which will have the index that was previous, the new index 
     * and the data that was moved.
     * @returns void
     */
    private sortChange(moved: any) {
        if (!this.item || !this.item.children) return;
        this.$emit('sorted-items', this.item.children);
        
        if (!this.sorting && !this.item.children) return;
        this.item.children = this.item.children.map((tree: TreeView, index: number) => ({ ...tree, order: index}));

        const sorted: sortedValue = {
          level: this.level,
          previousIndex: moved.moved.oldIndex,
          currentIndex: moved.moved.newIndex,
          item: moved.moved.element,
          tree: [...this.item.children.map((item: TreeView, index: number) => (item.id))]
        };

        this.emitSortedValue(sorted);
    }

    /**
     * @summary Method which returns the current state of the component for draggable
     * to rerender, which will then apply this state.
     * @returns defined object by draggable of the current state that we want to keep.
     */
    private getComponentData() {
      return {
        attrs: {
          isOpen: this.isOpen
        }
      }
    }

    private setIsOpen(state: boolean) {
      if (this.disableTree) return;
      this.isOpen = state;
    }

    private emitSortedValue(sorted: sortedValue) {
      this.$emit("sorted-value", sorted);
    }
}
